/**
 * Web Code Editor — Browse and edit project files with CodeMirror 6.
 */

import { useState, useCallback, useMemo, lazy, Suspense } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import {
  Folder, FileCode, ChevronRight, Save, X, FolderOpen,
} from "lucide-react";

const CodeMirrorEditor = lazy(() => import("../components/editor/CodeMirrorEditor"));

interface TreeItem {
  name: string;
  path: string;
  type: "file" | "dir";
  size: number;
  language: string | null;
}

interface FileData {
  path: string;
  content: string;
  language: string;
  size: number;
}

const PROJECT_ROOTS = ["/root/Projects", "/var/git-repos"];

import type { EditorLanguage } from "../components/editor/CodeMirrorEditor";

function cmLanguage(lang: string | null): EditorLanguage {
  if (!lang) return "plain";
  const map: Record<string, EditorLanguage> = {
    typescript: "typescript",
    typescriptreact: "typescriptreact",
    javascript: "javascript",
    json: "json",
    markdown: "markdown",
    css: "css",
    html: "html",
    python: "python",
    sql: "sql",
    yaml: "yaml",
    bash: "plain",
    rust: "plain",
    go: "plain",
    toml: "plain",
  };
  return map[lang] ?? "plain";
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function EditorRoute() {
  const [currentDir, setCurrentDir] = useState<string>(PROJECT_ROOTS[0]!);
  const [breadcrumb, setBreadcrumb] = useState<string[]>([PROJECT_ROOTS[0]!]);
  const [openFile, setOpenFile] = useState<FileData | null>(null);
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [cursorPos, setCursorPos] = useState({ line: 1, col: 1 });

  // Fetch directory tree
  const { data: treeData, isLoading: treeLoading } = useQuery<{ root: string; items: TreeItem[] }>({
    queryKey: ["editor", "tree", currentDir],
    queryFn: () => fetch(`/api/editor/tree?root=${encodeURIComponent(currentDir)}`).then((r) => r.json()),
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ path, content }: { path: string; content: string }) => {
      const res = await fetch("/api/editor/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, content }),
      });
      if (!res.ok) throw new Error("Save failed");
      return res.json();
    },
    onSuccess: () => {
      setEditedContent(null);
    },
  });

  const items = treeData?.items ?? [];
  const isWritable = currentDir.startsWith("/root/Projects");
  const hasChanges = editedContent !== null && openFile && editedContent !== openFile.content;

  const navigateToDir = useCallback((path: string) => {
    setCurrentDir(path);
    // Build breadcrumb from root
    const root = PROJECT_ROOTS.find((r) => path.startsWith(r)) ?? path;
    const parts = path.slice(root.length).split("/").filter(Boolean);
    const crumbs = [root];
    let cur = root;
    for (const p of parts) {
      cur += "/" + p;
      crumbs.push(cur);
    }
    setBreadcrumb(crumbs);
  }, []);

  const openFileHandler = useCallback(async (path: string) => {
    const res = await fetch(`/api/editor/file?path=${encodeURIComponent(path)}`);
    if (!res.ok) return;
    const data: FileData = await res.json();
    setOpenFile(data);
    setEditedContent(null);
  }, []);

  const handleSave = useCallback(() => {
    if (!openFile || editedContent === null) return;
    saveMutation.mutate({ path: openFile.path, content: editedContent });
  }, [openFile, editedContent, saveMutation]);

  const closeFile = useCallback(() => {
    setOpenFile(null);
    setEditedContent(null);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-[var(--bw-border)] flex items-center justify-between flex-wrap gap-2">
        <CRTHeader text="Editor" level={2} />
        <div className="flex gap-2">
          {PROJECT_ROOTS.map((root) => (
            <GlyphButton
              key={root}
              variant={currentDir.startsWith(root) ? "primary" : "secondary"}
              size="sm"
              onClick={() => navigateToDir(root)}
            >
              {root.split("/").pop()}
            </GlyphButton>
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* File tree panel */}
        <div className="w-64 border-r border-[var(--bw-border)] flex flex-col bg-[var(--bw-surface)] overflow-hidden">
          {/* Breadcrumb */}
          <div className="p-2 border-b border-[var(--bw-border)] flex items-center gap-1 overflow-x-auto text-[10px]">
            {breadcrumb.map((crumb, i) => (
              <span key={crumb} className="flex items-center gap-1 whitespace-nowrap">
                {i > 0 && <ChevronRight size={10} className="text-[var(--bw-muted)]" />}
                <button
                  onClick={() => navigateToDir(crumb)}
                  className="text-[var(--bw-accent)] hover:underline"
                >
                  {i === 0 ? crumb.split("/").pop() : crumb.split("/").pop()}
                </button>
              </span>
            ))}
          </div>

          {/* File list */}
          <div className="flex-1 overflow-y-auto p-1">
            {/* Parent dir */}
            {breadcrumb.length > 1 && (
              <button
                onClick={() => navigateToDir(breadcrumb[breadcrumb.length - 2]!)}
                className="w-full flex items-center gap-2 px-2 py-1 text-xs text-[var(--bw-muted)] hover:text-[var(--bw-fg)] hover:bg-[var(--bw-surface-2)]"
              >
                <Folder size={14} /> ..
              </button>
            )}

            {treeLoading && (
              <p className="text-xs text-[var(--bw-muted)] p-2 animate-pulse">Cargando...</p>
            )}

            {items.map((item) => (
              <button
                key={item.path}
                onClick={() => item.type === "dir" ? navigateToDir(item.path) : openFileHandler(item.path)}
                className={`
                  w-full flex items-center gap-2 px-2 py-1 text-xs transition-colors
                  ${openFile?.path === item.path
                    ? "bg-[var(--bw-accent)]/10 text-[var(--bw-accent)]"
                    : "text-[var(--bw-fg)] hover:bg-[var(--bw-surface-2)]"
                  }
                `}
              >
                {item.type === "dir" ? (
                  <FolderOpen size={14} className="text-[var(--bw-warn)] flex-shrink-0" />
                ) : (
                  <FileCode size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
                )}
                <span className="truncate flex-1 text-left">{item.name}</span>
                {item.type === "file" && (
                  <span className="text-[10px] text-[var(--bw-muted)]">{formatSize(item.size)}</span>
                )}
              </button>
            ))}

            {!treeLoading && items.length === 0 && (
              <p className="text-xs text-[var(--bw-muted)] p-2">Directorio vacío</p>
            )}
          </div>
        </div>

        {/* Editor panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {openFile ? (
            <>
              {/* File tab bar */}
              <div className="flex items-center justify-between border-b border-[var(--bw-border)] bg-[var(--bw-surface)] px-3 py-1">
                <div className="flex items-center gap-2 min-w-0">
                  <FileCode size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
                  <span className="text-xs text-[var(--bw-fg)] truncate font-mono">
                    {openFile.path.replace(/^\/root\/Projects\//, "")}
                  </span>
                  {hasChanges && (
                    <span className="w-2 h-2 rounded-full bg-[var(--bw-warn)] flex-shrink-0" title="Sin guardar" />
                  )}
                  <HexBadge variant="muted" size="sm">{openFile.language}</HexBadge>
                </div>
                <div className="flex items-center gap-1">
                  {isWritable && hasChanges && (
                    <GlyphButton size="sm" onClick={handleSave} disabled={saveMutation.isPending} icon={<Save size={12} />}>
                      {saveMutation.isPending ? "..." : "Guardar"}
                    </GlyphButton>
                  )}
                  <button onClick={closeFile} className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-fg)]">
                    <X size={14} />
                  </button>
                </div>
              </div>

              {/* CodeMirror editor */}
              <div className="flex-1 overflow-hidden">
                <Suspense fallback={<div className="p-4 text-[var(--bw-muted)] animate-pulse">Cargando editor...</div>}>
                  <CodeMirrorEditor
                    value={openFile.content}
                    onChange={(v) => setEditedContent(v)}
                    language={cmLanguage(openFile.language)}
                    readOnly={!isWritable}
                    onSave={handleSave}
                    onCursorChange={(line, col) => setCursorPos({ line, col })}
                    height="100%"
                  />
                </Suspense>
              </div>

              {/* Status bar */}
              <div className="flex items-center justify-between px-3 py-1 border-t border-[var(--bw-border)] bg-[var(--bw-surface)] text-[10px] text-[var(--bw-muted)] font-mono">
                <span>Ln {cursorPos.line}, Col {cursorPos.col}</span>
                <span>{formatSize(openFile.size)}</span>
                <span>{isWritable ? "WRITABLE" : "READ-ONLY"}</span>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FileCode size={48} className="mx-auto text-[var(--bw-muted)] mb-3" />
                <p className="text-sm text-[var(--bw-muted)]">Selecciona un archivo para editar</p>
                <p className="text-xs text-[var(--bw-muted)] mt-1">Ctrl+S para guardar</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
